
export const ymd = (date) => {
    const local = new Date(date);
    const year = local.getFullYear();
    const month = String(local.getMonth() + 1).padStart(2, '0');
    const day = String(local.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

export const toLocalStart = (date) => {
    if (!(date instanceof Date)) return null;
    const normalizedDate = new Date(date);
    normalizedDate.setHours(0, 0, 0, 0);
    return normalizedDate;
};

export const addOneDay = (ymdDate) => {
    if (!ymdDate) return null;
    const [year, month, day] = ymdDate.split("-").map(Number);
    if (!year || !month || !day) return null;
    const dateObj = new Date(year, month - 1, day + 1);
    return ymd(dateObj);
};

export const getDayName = (ymdDate) => {
    if (!ymdDate) return "-";
    const [year, month, day] = ymdDate.split("-").map(Number);
    if (!year || !month || !day) return "-";
    const dateObj = new Date(year, month - 1, day);
    return dateObj.toLocaleDateString("en-US", { weekday: "long" });
};

export const formatHolidayDate = (ymdDate) => {
    if (!ymdDate) return "-";
    const [year, month, day] = String(ymdDate).split("-").map(Number);
    if (!year || !month || !day) return ymdDate;
    const dateObj = new Date(year, month - 1, day);
    return dateObj.toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
    });
};

export const formatDateLabel = (item) => {
    if (!item) return "-";
    if (item?.date) return formatHolidayDate(item.date);
    if (item?.start_date && item?.end_date) {
        if (item.start_date === item.end_date) return formatHolidayDate(item.start_date);
        return `${formatHolidayDate(item.start_date)} to ${formatHolidayDate(item.end_date)}`;
    }
    return item?.start_date ? formatHolidayDate(item.start_date) : "-";
};

export const formatDayLabel = (item) => {
    if (!item) return "-";
    if (item?.date) return getDayName(item.date);
    if (item?.start_date && item?.end_date) {
        const startDay = getDayName(item.start_date);
        const endDay = getDayName(item.end_date);
        if (item.start_date === item.end_date) return startDay;
        return `${startDay} to ${endDay}`;
    }
    return getDayName(item?.start_date);
};

export const isAcademicYearCompleted = (academicYear) => (
    String(academicYear?.status || "").toLowerCase() === "completed"
);

export const isHolidayPast = (item) => {
    const holidayDate = item?.end_date || item?.date || item?.start_date || "";
    if (!holidayDate) return false;
    const today = new Date();
    const todayYMD = [
        today.getFullYear(),
        String(today.getMonth() + 1).padStart(2, "0"),
        String(today.getDate()).padStart(2, "0"),
    ].join("-");
    return holidayDate < todayYMD;
};

export const getHolidayTypeLabel = (item) => {
    const holiday = item?.holiday;
    if (!holiday?.is_half_day) {
        return "Full day";
    }
    return holiday?.half_day_type === "FIRST_HALF" ? "First Half" : "Second Half";
};

export const normalizeHolidayItems = (
    payload,
    { idKeys = [], includeAppliedToEntries = false } = {}
) => {
    const holidayItems = payload?.holiday_list || payload?.holidays || [];

    return holidayItems.flatMap((item, itemIndex) => {
        const date = item?.date || null;
        const startDate = item?.start_date || date || null;
        const endDate = item?.end_date || date || null;
        const sourceHolidays = item?.date_holidays || item?.holidays || [];

        return sourceHolidays.map((holiday, holidayIndex) => {
            const idKey = idKeys.find((key) => holiday?.[key]);
            const normalizedItem = {
                id: (idKey && holiday?.[idKey])
                    || item?.date_range_id
                    || date
                    || `${startDate || "no-date"}-${endDate || "no-end"}-${itemIndex}-${holidayIndex}`,
                date_range_id: item?.date_range_id || null,
                date,
                start_date: startDate,
                end_date: endDate,
                holiday,
            };

            if (!includeAppliedToEntries) {
                return normalizedItem;
            }

            const appliedTo = holiday?.applied_to;
            const appliedToEntries = Array.isArray(appliedTo) && appliedTo.length > 0
                ? appliedTo.map((entry) => ({
                    ...entry,
                    class_id: entry?.class_id || holiday?.class_id || null,
                    section_id: entry?.section_id || holiday?.section_id || null,
                    section_name: entry?.section_name || holiday?.section_name || null,
                    class_name: entry?.class_name || holiday?.class_name || null,
                }))
                : [{
                    _id: null,
                    class_id: holiday?.class_id || null,
                    section_id: holiday?.section_id || null,
                    section_name: holiday?.section_name || null,
                    class_name: holiday?.class_name || null,
                }];

            return {
                ...normalizedItem,
                appliedToEntries,
            };
        });
    });
};
